import React, {
  useCallback,
  useState,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { AutoAwesome as ReleaseNoteIcon } from '@mui/icons-material';
import format from 'string-template';
import moment from 'moment';
import * as Sentry from '@sentry/browser';

import { PrimaryButton } from '../../../../../components/Button/ActionButtons';
import ConfirmationModal from '../../../../../components/v2/ConfirmationModal';
import DialogModal from '../../../../../components/DialogModal';
import useToast from '../../../../hooks/useToast';
import { DateFormat } from '../../../../../utils/date';

import { ReleaseNoteStatus } from '../../../../../Model/ReleaseNote';
import releaseNotesTexts from '../../texts.json';
import { getStatusTag } from '../../utils';
import FeatureList from '../FeatureList';
import NotificationToggle from '../NotificationToggle';
import PhotoUpload from '../PhotoUpload';
import {
  ModalContainer,
  ModalHeader,
  ModalHeaderIcon,
  ModalHeaderContent,
  ModalTitleRow,
  ModalTitle,
  ModalSubtitle,
  ModalBody,
  ModalFooter,
} from './styles';
import texts from './texts.json';

const getApproveLabel = (isPhotoUploading, sendNotification) => {
  if (isPhotoUploading) return texts.uploadingPhoto;
  if (sendNotification) return texts.approveAndNotify;
  return texts.approve;
};

const RefineModal = ({ isOpen, note, onClose }) => {
  const { showToast } = useToast();

  const [features, setFeatures] = useState([]);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [sendNotification, setSendNotification] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPhotoUploading, setIsPhotoUploading] = useState(false);
  const [isRejectConfirmOpen, setIsRejectConfirmOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    if (!note) {
      setFeatures([]);
      setPhotoUrl(null);
      setSendNotification(false);
      return;
    }
    setFeatures(note.features.map((f, i) => ({ ...f, id: i })));
    setPhotoUrl(note.photoUrl);
    setSendNotification(note.sendNotification);
  }, [note]);

  const handleApprove = useCallback(async () => {
    if (!note) return;
    setIsSaving(true);
    try {
      const cleanFeatures = features.map(({ title, description }) => ({ title, description }));
      await note.approve(cleanFeatures, photoUrl, sendNotification);
      showToast(format(releaseNotesTexts.approveSuccess, { appVersion: note.appVersion }));
      onClose();
    } catch (error) {
      Sentry.captureException(error);
      showToast(releaseNotesTexts.approveError, { error: true });
    } finally {
      setIsSaving(false);
    }
  }, [note, features, photoUrl, sendNotification, showToast, onClose]);

  const openRejectConfirm = useCallback(() => {
    setRejectReason('');
    setIsRejectConfirmOpen(true);
  }, []);

  const closeRejectConfirm = useCallback(() => {
    setIsRejectConfirmOpen(false);
    setRejectReason('');
  }, []);

  const handleReject = useCallback(async () => {
    if (!note) return;
    setIsRejectConfirmOpen(false);
    setIsSaving(true);
    try {
      await note.reject(rejectReason || null);
      showToast(format(releaseNotesTexts.rejectSuccess, { appVersion: note.appVersion }));
      onClose();
    } catch (error) {
      Sentry.captureException(error);
      showToast(releaseNotesTexts.rejectError, { error: true });
    } finally {
      setIsSaving(false);
      setRejectReason('');
    }
  }, [note, rejectReason, showToast, onClose]);

  if (!note) return null;

  const isReadOnly = note.status !== ReleaseNoteStatus.PENDING;
  const generatedDate = note.generatedAt
    ? moment(note.generatedAt.toDate()).format(DateFormat.DEFAULT_DATE_FORMAT)
    : '';
  const featureCount = features.length;

  return (
    <>
      <DialogModal
        open={isOpen}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
      >
        <ModalContainer>
          <ModalHeader>
            <ModalHeaderIcon>
              <ReleaseNoteIcon fontSize="small" />
            </ModalHeaderIcon>
            <ModalHeaderContent>
              <ModalTitleRow>
                <ModalTitle>
                  {format(texts.modalTitle, { appVersion: note.appVersion })}
                </ModalTitle>
                {getStatusTag(note.status)}
              </ModalTitleRow>
              <ModalSubtitle>
                {format(texts.modalSubtitle, {
                  date: generatedDate,
                  featureCount,
                  featureWord: featureCount === 1 ? texts.featureWord : texts.featureWordPlural,
                })}
              </ModalSubtitle>
            </ModalHeaderContent>
          </ModalHeader>
          <ModalBody>
            <FeatureList
              features={features}
              onChange={setFeatures}
              isReadOnly={isReadOnly}
            />
            <NotificationToggle
              checked={sendNotification}
              onChange={setSendNotification}
              disabled={isReadOnly}
            />
            <PhotoUpload
              noteId={note.id}
              photoUrl={photoUrl}
              onChange={setPhotoUrl}
              onUploadingChange={setIsPhotoUploading}
              isReadOnly={isReadOnly}
            />
          </ModalBody>
          {!isReadOnly && (
            <ModalFooter>
              <PrimaryButton
                variant="danger"
                size="large"
                $fitToContent
                onClick={openRejectConfirm}
                disabled={!!isSaving}
              >
                {texts.reject}
              </PrimaryButton>
              <PrimaryButton
                variant="light"
                size="large"
                $fitToContent
                onClick={handleApprove}
                disabled={!!isSaving || isPhotoUploading || features.length === 0}
              >
                {getApproveLabel(isPhotoUploading, sendNotification)}
              </PrimaryButton>
            </ModalFooter>
          )}
        </ModalContainer>
      </DialogModal>
      <ConfirmationModal
        open={isRejectConfirmOpen}
        onClose={closeRejectConfirm}
        onConfirm={handleReject}
        title={releaseNotesTexts.rejectConfirmTitle}
        description={releaseNotesTexts.rejectConfirmDescription}
        showAdditionalInput
        inputLabel={releaseNotesTexts.rejectReasonLabel}
        onInputChange={setRejectReason}
      />
    </>
  );
};

RefineModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  note: PropTypes.shape({
    id: PropTypes.string,
    appVersion: PropTypes.string,
    status: PropTypes.string,
    generatedAt: PropTypes.object,
    photoUrl: PropTypes.string,
    features: PropTypes.array,
    sendNotification: PropTypes.bool,
    approve: PropTypes.func,
    reject: PropTypes.func,
  }),
  onClose: PropTypes.func.isRequired,
};

RefineModal.defaultProps = {
  note: null,
};

export default RefineModal;
